import { all, call, cancel, delay, fork, put, select, takeEvery } from 'redux-saga/effects';
import { getType } from 'deox';
import {
  initScenesLoader,
  setSceneSource,
  setSceneSourceTemp,
  setSceneSources,
  getSceneSources,
  ScenesLoader
} from '../scenesLoader';
import { Scene } from '../../types/Scene';
import { getScenes } from '../scenes';
import { setScenesDownloadingProgress } from '../sceneDownloadingProgress';
import {
  createDefaultProgress,
  downloadFromCDN,
  handleLoadingProgress,
  ProgressParts
} from 'utils/sagas/uploading';
import { isSameTaskId, isSceneExists } from 'utils/sagas/scenes';
import { deleteFile, isFileCached } from 'utils/fileCache';
import { getFileSize } from 'utils/apiWrapper';
import analytics, { ContentSource } from 'utils/analytics';
import { cancelled } from 'redux-saga-test-plan/matchers';
import { Licenses } from 'types/Licenses';
import { extensionFromString } from 'utils/formatFilename';
import { Task } from 'redux-saga';

export function* loadScene(sceneId: string, src: string, uploadTaskId: string) {
  const progress = createDefaultProgress();
  const isCached: boolean = yield call(isFileCached, src);
  const scenes: Scene[] = yield select(getScenes);
  const scene = scenes.find(scene => scene.id === sceneId);
  const source = ({
    [Licenses.TemplatePremium]: 'templates',
    [Licenses.PhotoChooserFree]: 'stock_images',
    [Licenses.PhotoChooserPremium]: 'stock_images',
    [Licenses.StockVideoFree]: 'stock_videos',
    [Licenses.StockVideoPremium]: 'stock_videos',
    [Licenses.ShutterstockVideo]: 'shutterstock_videos'
  } as { [key: string]: ContentSource })[scene.license];
  const analyticsStats = {
    downloadStartedTime: 0,
    downloadEndedTime: 0
  };
  const task: Task = yield fork(function* () {
    try {
      analyticsStats.downloadStartedTime = Date.now();
      const localSource: string = yield call(
        downloadFromCDN,
        src,
        handleLoadingProgress(progress, ProgressParts.Downloading)
      );
      analyticsStats.downloadEndedTime = Date.now();

      yield put(setSceneSourceTemp(localSource, sceneId));
      progress.taskDone = true;

      if (!isCached) {
        const size: number = yield call(getFileSize, src, num => num / 1024);
        const format = extensionFromString(src);

        const scenes: Scene[] = yield select(getScenes);
        const index = scenes.findIndex(scene => scene.id === sceneId);

        window.analytics.templateLoadSequence.push(index + 1);

        analytics.contentLoad({
          size,
          format,
          download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
          source,
          result: 'success'
        });
      }
    } catch (e) {
      progress.errorHappened = true;
      analyticsStats.downloadEndedTime = Date.now();
      const isCancelled = yield cancelled();
      if (isCancelled && !isCached) {
        const size: number = yield call(getFileSize, src, num => num / 1024);
        const format = extensionFromString(src);

        analytics.contentLoad({
          size,
          format,
          download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
          source,
          result: 'cancel'
        });
      }
    }
  });

  yield fork(function* () {
    while (!progress.errorHappened && !progress.taskDone) {
      // if uploadTaskId has changed, it means that new content is uploading to the scene
      const sceneExists = yield call(isSceneExists, sceneId);
      const sameUploadTask = yield call(isSameTaskId, sceneId, uploadTaskId);

      if (!sceneExists || !sameUploadTask) {
        yield cancel(task);
        yield cancel();
      }
      yield put(setScenesDownloadingProgress(progress.downloading, sceneId));

      yield delay(100);
    }
    yield put(setScenesDownloadingProgress(progress.downloading, sceneId));
  });
}

export function* loadMidRes({ midResSrc, id, uploadTaskId, src }: Scene) {
  const isCached: boolean = yield call(isFileCached, src);
  const scenes: Scene[] = yield select(getScenes);
  const scene = scenes.find(scene => scene.id === id);
  const source = ({
    [Licenses.TemplatePremium]: 'templates',
    [Licenses.PhotoChooserFree]: 'stock_images',
    [Licenses.PhotoChooserPremium]: 'stock_images',
    [Licenses.StockVideoFree]: 'stock_videos',
    [Licenses.StockVideoPremium]: 'stock_videos',
    [Licenses.ShutterstockVideo]: 'shutterstock_videos'
  } as { [key: string]: ContentSource })[scene.license];
  const analyticsStats = {
    downloadStartedTime: 0,
    downloadEndedTime: 0
  };
  const progress = createDefaultProgress();
  const task: Task = yield fork(function* () {
    try {
      analyticsStats.downloadStartedTime = Date.now();
      const localMidResSrc: string = yield call(downloadFromCDN, midResSrc);
      analyticsStats.downloadEndedTime = Date.now();
      yield put(setSceneSource(localMidResSrc, id));
      yield delay(200);
      yield call(deleteFile, src);

      if (!isCached) {
        const size: number = yield call(getFileSize, src, num => num / 1024);
        const format = extensionFromString(src);

        analytics.contentLoad({
          size,
          format,
          download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
          source,
          result: 'success'
        });
      }

      progress.taskDone = true;
    } catch (e) {
      progress.errorHappened = true;
      analyticsStats.downloadEndedTime = Date.now();
      const isCancelled = yield cancelled();
      if (isCancelled && !isCached) {
        const size: number = yield call(getFileSize, src, num => num / 1024);
        const format = extensionFromString(src);

        analytics.contentLoad({
          size,
          format,
          download_time: analyticsStats.downloadEndedTime - analyticsStats.downloadStartedTime,
          source,
          result: 'cancel'
        });
      }
    }
  });

  yield fork(function* () {
    while (!progress.errorHappened && !progress.taskDone) {
      const sceneExists = yield call(isSceneExists, id);
      const sameUploadTask = yield call(isSameTaskId, id, uploadTaskId);

      if (!sceneExists || !sameUploadTask) {
        yield cancel(task);
        yield cancel();
      }
      yield delay(100);
    }
  });
}

export function* initScenesSourcesSaga() {
  if (!window.analytics) window.analytics = {};
  window.analytics.templateLoadSequence = [];
  const loadStarted = Date.now();

  const scenes: Scene[] = yield select(getScenes);
  const sceneSources: ScenesLoader['sources'] = yield select(getSceneSources);

  const isSceneHasCorrectSource = ({ id, src }: Scene): boolean => sceneSources[id] === src;

  const sourceUpdates = scenes
    .filter(item => !isSceneHasCorrectSource(item))
    .map(scene => call(loadScene, scene.id, scene.src, scene.uploadTaskId));

  if (sourceUpdates.length > 0) {
    yield all(sourceUpdates);
    yield put(setSceneSources());
  }

  const loadEnded = Date.now();
  if (
    scenes.every(scene => scene.license === Licenses.TemplatePremium) &&
    window.analytics.templateLoadSequence.length === scenes.length &&
    window.analytics.templateId
  ) {
    let size = 0;
    for (const scene of scenes) {
      const fileSize: number = yield call(getFileSize, scene.src);
      size += fileSize;
    }
    yield call([analytics, analytics.templateLoad], {
      size,
      time: loadEnded - loadStarted,
      template_id: window.analytics.templateId,
      load_sequence: window.analytics.templateLoadSequence
    });
  }
}

export default function* scenesLoaderSaga() {
  yield takeEvery(getType(initScenesLoader), initScenesSourcesSaga);
}
